import { computed, ref } from 'vue'
import { useEventStore } from '@/stores/event-store'
import { eventFilesTable } from '../../client-db/src/db/schema'
import { localDB } from '@/migrate'
import { and, asc, eq } from 'drizzle-orm'
import {FileStorageService} from "@/services/FileStorageService.ts";

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024
const MAX_IMAGE_DIMENSION = 3200
const PROCESSED_IMAGE_QUALITY = 0.86

// Shared state so all consumers of this composable stay in sync (MainNav + Event view).
const eventThumbnails = ref<string[]>([])
const fullSizeImages = ref<(string | null)[]>([])
const loadingStates = ref<boolean[]>([])
const imageStorageIds = ref<string[]>([])
const selectedImageIndex = ref<number>(0)
const fileInput = ref<HTMLInputElement | null>(null)
const pendingImageUploads = ref<number>(0)
const isUploadingImages = computed(() => pendingImageUploads.value > 0)

const fileStorage = new FileStorageService({
  adapter: 'hybrid', // or 'hybrid' or 'server' or 'indexeddb
  serverEndpoint: '/api',
  authToken: localStorage.getItem('yourKey') || ''
})

export function useEventFiles() {
  const eventStore = useEventStore()

  // Load thumbnail images from database
  async function loadEventThumbnails() {
    if (eventStore.currentEvent?.id) {
      try {
        const [thumbnails, images] = await Promise.all([
          localDB
            .select()
            .from(eventFilesTable)
            .where(and(
              eq(eventFilesTable.eventId, eventStore.currentEvent.id),
              eq(eventFilesTable.fileType, 'THUMBNAIL')
            ))
            .orderBy(asc(eventFilesTable.id)),
          localDB
            .select()
            .from(eventFilesTable)
            .where(and(
              eq(eventFilesTable.eventId, eventStore.currentEvent.id),
              eq(eventFilesTable.fileType, 'IMAGE')
            ))
            .orderBy(asc(eventFilesTable.id)),
        ])

        // Get URLs from storage service
        eventThumbnails.value = await Promise.all(
          thumbnails.map(async (thumbnail) => {
            try {
              return await fileStorage.getFileUrl(thumbnail.storageId!)
            } catch (error) {
              console.error(`Failed to get URL for thumbnail ${thumbnail.id}:`, error)
              return '' // Return empty string for failed loads
            }
          })
        )

        // Initialize arrays for full-size images and loading states
        fullSizeImages.value = new Array(thumbnails.length).fill(null)
        loadingStates.value = new Array(thumbnails.length).fill(false)
        imageStorageIds.value = images.map((image) => image.storageId || '')
      } catch (error) {
        console.error('Failed to load event thumbnails:', error)
      }
    }
  }

  // Load full-size image for modal display
  async function loadFullSizeImage(index: number) {
    if (fullSizeImages.value[index] || loadingStates.value[index]) {
      return // Already loaded or loading
    }

    try {
      loadingStates.value[index] = true
      const storageId = imageStorageIds.value[index]
      if (storageId) {
        fullSizeImages.value[index] = await fileStorage.getFileUrl(storageId)
      }
    } catch (error) {
      console.error('Failed to load full-size image:', error)
    } finally {
      loadingStates.value[index] = false
    }
  }

  async function handleImageFilesUpload(files: File[]) {
    if (!files.length || !eventStore.currentEvent?.id) return

    const validFiles = files.filter(file => file.type.startsWith('image/'))

    if (validFiles.length === 0) {
      console.log('Please select at least one image file.')
      return
    }

    pendingImageUploads.value += validFiles.length

    try {
      let completed = 0
      const total = validFiles.length

      for (const file of validFiles) {
        try {
          if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            console.warn(`Skipping ${file.name}: file exceeds ${Math.round(MAX_UPLOAD_SIZE_BYTES / 1024 / 1024)}MB limit`)
            continue
          }

          const processedImageFile = await createProcessedUploadImage(file)

          if (processedImageFile.size > MAX_UPLOAD_SIZE_BYTES) {
            console.warn(`Skipping ${file.name}: processed image still exceeds ${Math.round(MAX_UPLOAD_SIZE_BYTES / 1024 / 1024)}MB limit`)
            continue
          }

          // Create thumbnail
          const thumbnailData = await createThumbnail(file)
          const thumbnailBlob = new Blob([thumbnailData], { type: 'image/jpeg' })

          // Store both files using the storage service
          const thumbnailStorageId = await fileStorage.storeThumbnail(
            new File([thumbnailBlob], `thumbnail_${file.name}`, { type: 'image/jpeg' }),
            eventStore.currentEvent.id
          )

          const imageStorageId = await fileStorage.storeImage(processedImageFile, eventStore.currentEvent.id)

          // Save metadata to database
          await saveThumbnailToDatabase(file, thumbnailStorageId, thumbnailData.length)
          await saveImageToDatabase(processedImageFile, imageStorageId)

          completed++
          console.log(`Uploaded ${completed}/${total} images`)

          await loadEventThumbnails()
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error)
        } finally {
          pendingImageUploads.value = Math.max(0, pendingImageUploads.value - 1)
        }
      }

      if (completed === total) {
        console.log(`Successfully uploaded all ${total} images!`)
      } else {
        console.log(`Uploaded ${completed}/${total} images. Some uploads failed.`)
      }
    } catch (error) {
      console.error('Failed to process images:', error)
      console.log('Failed to upload images. Please try again.')
    }

  }

  // Handle image upload from file input
  async function handleImageUpload(event: Event) {
    const target = event.target as HTMLInputElement
    const files = target.files

    if (!files) return

    await handleImageFilesUpload(Array.from(files))
    target.value = ''
  }

  function loadImageElement(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file)
      const img = new Image()

      img.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(img)
      }

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('Failed to load image'))
      }

      img.src = objectUrl
    })
  }

  function getResizedDimensions(width: number, height: number, maxSize: number) {
    if (width <= maxSize && height <= maxSize) {
      return { width, height }
    }

    if (width > height) {
      return {
        width: maxSize,
        height: Math.round((height * maxSize) / width),
      }
    }

    return {
      width: Math.round((width * maxSize) / height),
      height: maxSize,
    }
  }

  async function renderAsJpeg(file: File, maxSize: number, quality: number): Promise<Blob> {
    const img = await loadImageElement(file)
    const { width, height } = getResizedDimensions(img.width, img.height, maxSize)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Canvas not supported')
    }

    canvas.width = width
    canvas.height = height
    ctx.drawImage(img, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', quality)
    })

    if (!blob) {
      throw new Error('Failed to create processed image')
    }

    return blob
  }

  async function createProcessedUploadImage(file: File): Promise<File> {
    const processedBlob = await renderAsJpeg(file, MAX_IMAGE_DIMENSION, PROCESSED_IMAGE_QUALITY)
    const baseName = file.name.replace(/\.[^/.]+$/, '')
    return new File([processedBlob], `${baseName}.jpg`, { type: 'image/jpeg' })
  }

  // Create thumbnail from file
  function createThumbnail(file: File): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      renderAsJpeg(file, 400, 0.8)
        .then((blob) => blob.arrayBuffer())
        .then((arrayBuffer) => resolve(new Uint8Array(arrayBuffer)))
        .catch(reject)
    })
  }

  // Save thumbnail metadata to database
  async function saveThumbnailToDatabase(originalFile: File, storageId: string, thumbnailSize: number) {
    if (!eventStore.currentEvent?.id) return

    try {
      const fileExtension = originalFile.name.split('.').pop() || ''
      const fileSizeMB = (thumbnailSize / 1024 / 1024).toFixed(2)

      await localDB
        .insert(eventFilesTable)
        .values({
          eventId: eventStore.currentEvent.id,
          storageId,
          fileType: 'THUMBNAIL',
          mimeType: 'image/jpeg',
          ext: fileExtension,
          fileSize: fileSizeMB,
          filename: `thumbnail_${originalFile.name}`,
        })
    } catch (error) {
      console.error('Failed to save thumbnail to database:', error)
      throw error
    }
  }

  // Save image metadata to database
  async function saveImageToDatabase(file: File, storageId: string) {
    if (!eventStore.currentEvent?.id) return

    try {
      const fileExtension = file.name.split('.').pop() || ''
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2)

      await localDB
        .insert(eventFilesTable)
        .values({
          eventId: eventStore.currentEvent.id,
          storageId,
          fileType: 'IMAGE',
          mimeType: file.type,
          ext: fileExtension,
          fileSize: fileSizeMB,
          filename: file.name,
        })
    } catch (error) {
      console.error('Failed to save image to database:', error)
      throw error
    }
  }

  // Delete current image from modal
  async function deleteCurrentImage(modalRef: { close: () => void }) {
    if (!eventStore.currentEvent?.id || eventThumbnails.value.length === 0) return

    try {
      const thumbnails = await localDB
        .select()
        .from(eventFilesTable)
        .where(and(
          eq(eventFilesTable.eventId, eventStore.currentEvent.id),
          eq(eventFilesTable.fileType, 'THUMBNAIL')
        ))

      const images = await localDB
        .select()
        .from(eventFilesTable)
        .where(and(
          eq(eventFilesTable.eventId, eventStore.currentEvent.id),
          eq(eventFilesTable.fileType, 'IMAGE')
        ))

      if (thumbnails.length > selectedImageIndex.value && images.length > selectedImageIndex.value) {
        const thumbnailToDelete = thumbnails[selectedImageIndex.value]
        const imageToDelete = images[selectedImageIndex.value]

        // Delete from storage service
        await fileStorage.deleteFile(thumbnailToDelete.storageId!)
        await fileStorage.deleteFile(imageToDelete.storageId!)

        // Delete from database
        await localDB
          .delete(eventFilesTable)
          .where(eq(eventFilesTable.id, thumbnailToDelete.id))

        await localDB
          .delete(eventFilesTable)
          .where(eq(eventFilesTable.id, imageToDelete.id))
      }

      await loadEventThumbnails()

      if (eventThumbnails.value.length === 0) {
        modalRef.close()
      } else {
        if (selectedImageIndex.value >= eventThumbnails.value.length) {
          selectedImageIndex.value = eventThumbnails.value.length - 1
        }
      }
    } catch (error) {
      console.error('Failed to delete image:', error)
      console.log('Failed to delete image. Please try again.')
    }
  }

  // Open image modal and start loading full-size image
  function openImageModal(imageIndex: number, modalRef: { showModal: () => void }) {
    selectedImageIndex.value = imageIndex
    modalRef.showModal()
    loadFullSizeImage(imageIndex)
  }

  // Get the appropriate image source (full-size if loaded, otherwise thumbnail)
  function getImageSource(index: number): string {
    return fullSizeImages.value[index] || eventThumbnails.value[index]
  }

  // Check if image is currently loading
  function isImageLoading(index: number): boolean {
    return loadingStates.value[index] && !fullSizeImages.value[index]
  }

  // Trigger file input click
  function triggerImageUpload() {
    fileInput.value?.click()
  }

  function clearEventFiles() {
    eventThumbnails.value = []
    fullSizeImages.value = []
    loadingStates.value = []
    imageStorageIds.value = []
    selectedImageIndex.value = 0
  }

  return {
    // State
    eventThumbnails,
    fullSizeImages,
    loadingStates,
    selectedImageIndex,
    fileInput,
    pendingImageUploads,
    isUploadingImages,

    // Methods
    loadEventThumbnails,
    loadFullSizeImage,
    openImageModal,
    getImageSource,
    isImageLoading,
    deleteCurrentImage,
    triggerImageUpload,
    handleImageUpload,
    handleImageFilesUpload,
    clearEventFiles,

    // Backward compatibility
    eventImages: eventThumbnails,
    loadEventImages: loadEventThumbnails,
  }
}
